import { DEFAULT_TIMEOUT_MS, isTimeoutError } from './_transport';

export interface CognitoTokenSuccess {
  ok: true;
  idToken: string;
  refreshToken?: string;
}
export type CognitoTokenFailure =
  | { ok: false; kind: 'oauth'; error: string }
  | { ok: false; kind: 'transport'; code: 'network' | 'timeout' };
export type CognitoTokenResult = CognitoTokenSuccess | CognitoTokenFailure;

export class CognitoTokenClient {
  private readonly hostedUiUrl: string;

  private readonly clientId: string;

  private readonly timeoutMs: number;

  constructor(config: { hostedUiUrl: string; clientId: string; timeoutMs?: number }) {
    this.hostedUiUrl = config.hostedUiUrl;
    this.clientId = config.clientId;
    this.timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  async exchangeCode(params: { code: string; codeVerifier: string; redirectUri: string }): Promise<CognitoTokenResult> {
    return this.postToken({
      grant_type: 'authorization_code',
      code: params.code,
      code_verifier: params.codeVerifier,
      redirect_uri: params.redirectUri,
    });
  }

  async refresh(params: { refreshToken: string }): Promise<CognitoTokenResult> {
    return this.postToken({
      grant_type: 'refresh_token',
      refresh_token: params.refreshToken,
    });
  }

  async revoke(params: { token: string }): Promise<void> {
    const response = await fetch(`${this.hostedUiUrl}/oauth2/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: params.token, client_id: this.clientId }),
      signal: AbortSignal.timeout(this.timeoutMs),
      keepalive: true,
    });
    if (!response.ok) {
      throw new Error(`Cognito revoke failed with status ${response.status}`);
    }
  }

  private async postToken(fields: Record<string, string>): Promise<CognitoTokenResult> {
    let response: Response;
    try {
      response = await fetch(`${this.hostedUiUrl}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ client_id: this.clientId, ...fields }),
        signal: AbortSignal.timeout(this.timeoutMs),
      });
    } catch (error) {
      return { ok: false, kind: 'transport', code: isTimeoutError(error) ? 'timeout' : 'network' };
    }

    let body: Record<string, unknown>;
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch (error) {
      console.error('Cognito token endpoint returned a non-JSON response', error);
      return { ok: false, kind: 'transport', code: 'network' };
    }

    if (!response.ok) {
      console.error('Cognito token endpoint returned an OAuth error', body.error, response.status);
      return { ok: false, kind: 'oauth', error: typeof body.error === 'string' ? body.error : 'server_error' };
    }
    if (typeof body.id_token !== 'string') {
      console.error('Cognito token endpoint returned a 200 response with no id_token');
      return { ok: false, kind: 'transport', code: 'network' };
    }
    return {
      ok: true,
      idToken: body.id_token,
      refreshToken: typeof body.refresh_token === 'string' ? body.refresh_token : undefined,
    };
  }
}
